// Components==============
import storeEvidence from 'actions/evidence/store';
import destroyEvidence from 'actions/evidence/destroy';
import destroyAttendance from 'actions/training/attendance/destroy';
import storeTrainingAttendance from 'actions/training/attendance/store';
import destroyTrainingSubscription from 'actions/training/subscription/destroy';
import showTrainingSubscription from 'actions/training/subscription/show';
import destroyTraining from 'actions/training/training/destroy';
import indexTraining from 'actions/training/training/index';
import storeTraining from 'actions/training/training/store';
import updateTraining from 'actions/training/training/update';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import { useHistory } from 'react-router';
import { useConfirm } from 'hooks/useConfirm';
import { useTranslate } from 'hooks/useTranslate';
import { TrainingDetailsContextType } from '../TrainingDetails';
// =========================

export type TrainingDetailFunctions = ReturnType<
  typeof useTrainingDetailsFunctions
>;

export function useTrainingDetailsFunctions({
  training,
  sessionId,
  subscriber,
}: TrainingDetailsContextType) {
  const dispatch = useReduxAction();
  const history = useHistory();
  const confirm = useConfirm();

  const userId = useRedux((state) => state.authentication?.user?.id);
  const translate = useTranslate();

  function handleDelete() {
    const action = () =>
      !training?.subscription_id || training?.owner_id === userId
        ? dispatch(destroyTraining({ trainingId: training?.id }))
        : dispatch(
            destroyTrainingSubscription({
              trainingId: training?.id,
              subscriptionId: training.subscription_id,
            })
          );

    action().then(() => history.push(`/werkplaats/mijn-trainingen`));
  }

  function handlePublish(visibility: 'private' | 'org' | 'public') {
    dispatch(
      updateTraining({
        trainingId: training?.id,
        is_published: 1,
        visibility,
      })
    ).then(() => dispatch(indexTraining()));
  }

  function handleDuplicate() {
    dispatch(
      storeTraining({
        cloneFromId: training?.id,
        editable: true,
      })
    ).then((clonedTraining: TrainingEntity) =>
      history.push(`/werkplaats/training-bouwer/${clonedTraining.id}`)
    );
  }

  function handleEdit() {
    history.push(`/werkplaats/training-bouwer/${training?.id}`);
  }

  function toggleDone({
    done,
    lessonId,
    attendanceId,
  }: {
    done: boolean;
    lessonId: number;
    attendanceId?: number;
  }) {
    return dispatch(
      done
        ? destroyAttendance({
            trainingId: training?.id,
            sessionId,
            lessonId,
            attendanceId,
          })
        : storeTrainingAttendance({
            trainingId: training?.id,
            sessionId,
            lessonId,
            lesson_status: 'viewed',
          })
    ).then(() =>
      dispatch(
        showTrainingSubscription({
          trainingId: training?.id,
          subscriptionId: subscriber?.id,
        })
      )
    );
  }

  function handleEvidenceUpload(
    e: React.ChangeEvent<HTMLInputElement>,
    lesson: LessonEntity
  ) {
    const file = e?.target?.files?.[0];

    const attendance = subscriber?.attendances?.find(
      (a) => a.lesson_id === lesson.id
    );

    if (!attendance) {
      dispatch(
        storeTrainingAttendance({
          trainingId: training?.id,
          sessionId,
          lessonId: lesson.id,
          lesson_status: 'viewed',
        })
      )
        .then((res: TrainingAttendanceEntity) =>
          dispatch(
            storeEvidence({
              processId: res?.id,
              processType: 'App\\Training\\Attendance',
              file,
            })
          )
        )
        .then(() =>
          dispatch(
            showTrainingSubscription({
              trainingId: training?.id,
              subscriptionId: subscriber?.id,
            })
          )
        );
    } else {
      dispatch(
        storeEvidence({
          processId: attendance?.id,
          processType: 'App\\Training\\Attendance',
          file,
        })
      ).then(() =>
        dispatch(
          showTrainingSubscription({
            trainingId: training?.id,
            subscriptionId: subscriber?.id,
          })
        )
      );
    }
  }

  const handleEvidenceDelete = ({ evidenceId }: { evidenceId: number }) =>
    confirm(translate('work.confirm-delete-evidence-and-feedback'), () => {
      dispatch(destroyEvidence({ evidenceId })).then(() =>
        dispatch(
          showTrainingSubscription({
            trainingId: training?.id,
            subscriptionId: subscriber?.id,
          })
        )
      );
    });

  return {
    handleDelete,
    handlePublish,
    handleDuplicate,
    handleEdit,
    toggleDone,
    handleEvidenceUpload,
    handleEvidenceDelete,
  };
}
